import { useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import classNames from 'classnames';
import { Select } from 'alurkerja-ui';
import { AlurkerjaInputType } from '../../types/InputType';
import useFormEditorState from '../../state/formeditor.state';
import { Label } from 'bpmn-js/lib/model/Types';

interface RemoteSelectProps {
  props: AlurkerjaInputType;
}

export const RemoteSelect = ({ props }: RemoteSelectProps) => {
  const {
    item,
    form: { control, getValues },
  } = props;

  const { axiosInstance } = useFormEditorState();
  const [options, setOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const defaultValue = getValues(item.name);

  useEffect(() => {
    if (item.data_url) {
      if(axiosInstance) {
        fetchRemoteData();
      }
      fetchRemoteData();
    }
  }, [item.data_url , axiosInstance]);

  const fetchRemoteData = async () => {
    if (!item.data_url) return;

    setLoading(true);
    setError(null);

    try {
      const response = await axiosInstance.get(item.data_url);
      let data = response.data;

      // Navigate to nested data if root_data is specified
      if (item.root_data) {
        const paths = item.root_data.split('.');
        for (const path of paths) {
          if (data && typeof data === 'object' && path in data) {
            data = data[path];
          } else {
            throw new Error(`Path '${item.root_data}' not found in response`);
          }
        }
      }

      if (Array.isArray(data)) {
        const formattedOptions = data.map((option) => ({
          value: String(getLabelValue(option, item.select_remote_value_field || 'id')),
          label: String(getLabelValue(option, item.select_remote_label_field || 'name')),
        }));
        setOptions(formattedOptions);
      } else {
        throw new Error('Data is not an array');
      }
    } catch (err: any) {
      console.error('Error fetching remote data:', err);
      setError(err.message || 'Failed to fetch data');
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  const getLabelValue = (option: any, field: string) => {
    if (!field) return '';
    const paths = field.split('.');
    let value = option;
    for (const path of paths) {
      if (value && typeof value === 'object' && path in value) {
        value = value[path];
      } else {
        return '';
      }
    }
    return value;
  };

  const getCurrentLanguage = () => {
    // You can get this from context or state management
    return 'en'; // Default to English
  };

  const getPlaceholder = () => {
    const lang = getCurrentLanguage();
    
    if (typeof item.placeholder === 'object' && item.placeholder !== null) {
      return item.placeholder[lang as keyof typeof item.placeholder] || (item.placeholder as Label).en || 'Select an option';
    }
    return item.placeholder || 'Select an option';
  };

  const getLabel = () => {
    const lang = getCurrentLanguage();
    if (typeof item.label === 'object' && item.label !== null) {
      return item.label[lang as keyof typeof item.label] || item.label.en || '';
    }
    return item.label || '';
  };

  return (
    <div>
      <label
        className={classNames('font-medium text-sm', {
          'after:content-["*"] after:text-red-400 after:text-sm': item.constraints?.required,
        })}
        htmlFor={item.name}
      >
        {getLabel()}
      </label>
      <Controller
        name={item.name}
        control={control}
        defaultValue={defaultValue || ''}
        render={({ field, fieldState }) => {
          const selectedOption = options.find((opt) => opt.value === field.value);

          return (
            <div>
              <Select
                value={selectedOption}
                options={options}
                onChange={(e: any) => {
                  const value = e?.value || '';
                  field.onChange(value);
                }}
                isSearchable={true}
                isDisabled={item.disabled || loading}
                placeholder={loading ? 'Loading...' : error ? 'Error loading data' : getPlaceholder()}
                isLoading={loading}
              />
              {fieldState.error && (
                <p className="text-sm text-red-500 mt-1">{fieldState.error.message}</p>
              )}
              {error && !fieldState.error && (
                <p className="text-sm text-orange-500 mt-1">{error}</p>
              )}
            </div>
          );
        }}
      />
    </div>
  );
};
